import React, { useEffect, useState } from "react";
import { Card, Row, Col, Badge, Button, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { CompanyService } from "Services/CompanyService";
import { toast } from "react-toastify";
import "./company.css";
import CompanyPlan from "./CompanyPlan";

interface CompanyType {
  _id: string;
  company_code: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  controls: {
    max_candidates: number;
    max_exams: number;
    max_proctors: number;
  };
  ai_features: string[];
}

const ViewCompany = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [company, setCompany] = useState<CompanyType | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchCompanyDetails = async () => {
    try {
      setLoading(true);

      if (!id) return;

      const res = await CompanyService.getCompanyById(id);

      if (res?.status === 200) {
        const data = res?.data?.data;

        setCompany({
          ...data,
          status: data.status?.toUpperCase(),
          createdAt: new Date(data.createdAt).toLocaleDateString(),
          updatedAt: new Date(data.updatedAt).toLocaleDateString(),
        });
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to fetch company details",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanyDetails();
  }, [id]);

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!company) return null;

  return (
    <div className="company-page">
      <Card className="shadow-sm border-0 company-card">
        <Card.Body>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h4 className="fw-bold mb-1">Company Details</h4>
              <small className="text-muted">
                Complete information about company
              </small>
            </div>

            <Button variant="light" onClick={() => navigate(-1)}>
              ← Back
            </Button>
          </div>

          {/* Basic Info */}
          <Card className="mb-4 info-card">
            <Card.Body>
              <h6 className="section-title mb-3">Basic Information</h6>

              <Row>
                <Col md={4}>
                  <p className="label">Company Code</p>
                  <p className="value">{company.company_code}</p>
                </Col>

                <Col md={4}>
                  <p className="label">Company Name</p>
                  <p className="value">{company.name}</p>
                </Col>

                <Col md={4}>
                  <p className="label">Status</p>
                  <Badge
                    bg={company.status === "ACTIVE" ? "success" : "danger"}
                    className="px-3 py-2"
                  >
                    {company.status}
                  </Badge>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col md={4}>
                  <p className="label">Email</p>
                  <p className="value">{company.email}</p>
                </Col>

                <Col md={4}>
                  <p className="label">Phone</p>
                  <p className="value">{company.phone}</p>
                </Col>

                <Col md={4}>
                  <p className="label">Address</p>
                  <p className="value">{company.address}</p>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col md={4}>
                  <p className="label">Created At</p>
                  <p className="value">{company.createdAt}</p>
                </Col>

                <Col md={4}>
                  <p className="label">Updated At</p>
                  <p className="value">{company.updatedAt}</p>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Controls */}
          <Card className="mb-4 info-card">
            <Card.Body>
              <h6 className="section-title mb-3">Controls</h6>

              <Row>
                <Col md={4}>
                  <p className="label">Max Candidates</p>
                  <p className="value">{company.controls?.max_candidates}</p>
                </Col>

                <Col md={4}>
                  <p className="label">Max Exams</p>
                  <p className="value">{company.controls?.max_exams}</p>
                </Col>

                <Col md={4}>
                  <p className="label">Max Proctors</p>
                  <p className="value">{company.controls?.max_proctors}</p>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <CompanyPlan companyId={id} />

          {/* AI Features */}
          {/* <Card className="info-card">
            <Card.Body>
              <h6 className="section-title mb-3">AI Features</h6>

              {company.ai_features?.map((feature, index) => (
                <Badge
                  key={index}
                  bg="dark"
                  className="me-2 px-3 py-2 text-capitalize"
                >
                  {feature.replace("_", " ")}
                </Badge>
              ))}
            </Card.Body>
          </Card> */}
        </Card.Body>
      </Card>
    </div>
  );
};

export default ViewCompany;
